import {
    Box,
    Chip,
    Grid2 as Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import React, { useMemo } from 'react';
import { useUser } from 'src/allauth/auth';
import { FadedPaper } from 'src/theme/StyledFields';
import { AthletePaymentAssignment, PaymentAssignments } from '../object-actions/types/types';
import PaymentAssignmentCard from './PaymentAssignmentCard';
import ProgramProgressStepper from './ProgramProgressStepper';
import ProgramProgressStepperCoach from './ProgramProgressStepperCoach';
import ResponseGraphs, { AssessmentDomainMap } from './ResponseGraphs';

interface AthleteDetailViewProps {
    assignment: AthletePaymentAssignment;
    extras?: React.ReactNode;
}

export function getCategoryDisplayName(fieldName: string): string {
    const domain = AssessmentDomainMap[fieldName];
    if (domain) return domain.name;
    return fieldName
        .replace('category_', '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
}

function calculateChange(current: number | null | undefined, previous: number | null | undefined): number | null {
    if (current === null || current === undefined || previous === null || previous === undefined) {
        return null;
    }
    return current - previous;
}

const categoryFields = Object.keys(AssessmentDomainMap);

const AthleteDetailView: React.FC<AthleteDetailViewProps> = ({ assignment, extras }) => {
    const user = useUser();

    const submissionsWithScores = useMemo(() => {
        if (!assignment?.submissions) return [];
        return assignment.submissions
            .filter(sub => (sub.entity as any)?.submitted_at)
            .sort((a, b) => {
                const dateA = (a.entity as any)?.submitted_at ? new Date((a.entity as any).submitted_at).getTime() : 0;
                const dateB = (b.entity as any)?.submitted_at ? new Date((b.entity as any).submitted_at).getTime() : 0;
                return dateA - dateB;
            });
    }, [assignment?.submissions]);

    const athlete = assignment.athlete;
    const hasEntityScores = athlete?.entity && (athlete.entity as any)?.category_leadership;

    return (
        <Box data-testid="athlete-detail-view">
            <FadedPaper elevation={0} sx={{ mb: 4, p: 2 }}>
                <Grid container spacing={1} justifyContent="space-between">
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <PaymentAssignmentCard extras={extras} assignment={assignment as unknown as PaymentAssignments} />
                    </Grid>
                    {hasEntityScores && athlete?.entity && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <ResponseGraphs
                                entity={athlete.entity}
                                submissions={assignment.submissions}
                                height={250}
                            />
                        </Grid>
                    )}
                </Grid>
                <Typography variant="h5" component="h2" sx={{ mt: 3, mb: 1 }}>
                    {athlete?.id == user?.id ? 'My Progress' : `Program Progress: ${athlete?.str ?? 'Assignment'}`}
                </Typography>
                <Box data-testid="progress-report-stepper">
                    {assignment.my_roles.includes('coach') ||
                        assignment.my_roles.includes('parent') ||
                        assignment.my_roles.includes('payer') ? (
                        <ProgramProgressStepperCoach assignment={assignment} />
                    ) : (
                        <ProgramProgressStepper assignment={assignment} />
                    )}
                </Box>
            </FadedPaper>

            {submissionsWithScores.length > 0 && (
                <FadedPaper elevation={0} sx={{ mb: 4, p: 2 }} data-testid="progress-report-domain-scores">
                    <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
                        Domain Scores by Assessment
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Domain</TableCell>
                                    {submissionsWithScores.map((submission, idx) => {
                                        const entity = submission.entity as any;
                                        const assessmentTitle = entity?.assessment ?
                                            (typeof entity.assessment === 'object' && 'str' in entity.assessment
                                                ? entity.assessment.str
                                                : `Assessment ${idx + 1}`)
                                            : `Assessment ${idx + 1}`;
                                        return (
                                            <TableCell key={submission.id} align="right">
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {assessmentTitle}
                                                    </Typography>
                                                    {entity?.submitted_at && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(entity.submitted_at).toLocaleDateString()}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        );
                                    })}
                                    {submissionsWithScores.length > 1 && (
                                        <TableCell align="right" data-testid="progress-report-delta">
                                            <Typography variant="body2" fontWeight="bold">
                                                Change
                                            </Typography>
                                        </TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {categoryFields.map((fieldName) => {
                                    const displayName = getCategoryDisplayName(fieldName);
                                    const firstEntity = submissionsWithScores[0]?.entity as any;
                                    const lastEntity = submissionsWithScores[submissionsWithScores.length - 1]?.entity as any;
                                    const firstScore = firstEntity?.[fieldName] as number | null | undefined;
                                    const lastScore = lastEntity?.[fieldName] as number | null | undefined;
                                    const change = submissionsWithScores.length > 1 ? calculateChange(lastScore, firstScore) : null;

                                    return (
                                        <TableRow key={fieldName}>
                                            <TableCell component="th" scope="row">
                                                {displayName}
                                            </TableCell>
                                            {submissionsWithScores.map((submission) => {
                                                const entity = submission.entity as any;
                                                const score = entity?.[fieldName] as number | null | undefined;
                                                return (
                                                    <TableCell key={submission.id} align="right">
                                                        {score !== null && score !== undefined ? score.toFixed(2) : '-'}
                                                    </TableCell>
                                                );
                                            })}
                                            {submissionsWithScores.length > 1 && (
                                                <TableCell align="right">
                                                    {change !== null ? (
                                                        <Chip
                                                            label={change > 0 ? `+${change.toFixed(2)}` : change.toFixed(2)}
                                                            size="small"
                                                            color={change > 0 ? 'success' : change < 0 ? 'error' : 'default'}
                                                            variant="outlined"
                                                        />
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </FadedPaper>
            )}
        </Box>
    );
};

export default AthleteDetailView;
